/**
 * Canonical 5-star rating display — the single surface for showing a star
 * rating anywhere (course cards, course-detail hero, grids…).
 *
 * <p>The fill color comes from the site-builder theme token `--color-stars`
 * (with an `#EF8735` literal fallback) so it stays styled before a tenant
 * customises the theme. Fills are snapped to half-steps, so a 3.5 shows three
 * full stars + a half-filled fourth, and a 4.8 rounds up to five.
 *
 * <p>{@link StarRating} is the INTERACTIVE sibling — empty by default, the
 * viewer hovers/clicks to pick a whole-star value (used by the course-detail
 * comment composer).
 */
'use client';

import { useState, type CSSProperties } from 'react';

/**
 * Bootstrap Icons star glyphs (viewBox 0 0 16 16), the same set the
 * react-flexible-star-rating library uses. Each is a single self-contained
 * path so the half star is a purpose-drawn shape — left half solid, right half
 * a clean outline — rather than a gradient/clip hack that can squish or seam.
 */
const STAR_EMPTY =
  'M2.866 14.85c-.078.444.36.791.746.593l4.39-2.256 4.389 2.256c.386.198.824-.149.746-.592l-.83-4.73 3.522-3.356c.33-.314.16-.888-.282-.95l-4.898-.696L8.465.792a.513.513 0 0 0-.927 0L5.354 5.12l-4.898.696c-.441.062-.612.636-.283.95l3.523 3.356-.83 4.73zm4.905-2.767-3.686 1.894.694-3.957a.565.565 0 0 0-.163-.505L1.71 6.745l4.052-.576a.525.525 0 0 0 .393-.288L8 2.223l1.847 3.658a.525.525 0 0 0 .393.288l4.052.575-2.906 2.77a.565.565 0 0 0-.163.506l.694 3.957-3.686-1.894a.503.503 0 0 0-.461 0z';
const STAR_HALF =
  'M5.354 5.119 7.538.792A.516.516 0 0 1 8 .5c.183 0 .366.097.465.292l2.184 4.327 4.898.696A.537.537 0 0 1 16 6.32a.548.548 0 0 1-.17.445l-3.523 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256a.52.52 0 0 1-.146.05c-.342.06-.668-.254-.6-.642l.83-4.73L.173 6.765a.55.55 0 0 1-.172-.403.58.58 0 0 1 .085-.302.513.513 0 0 1 .37-.245l4.898-.696zM8 12.027a.5.5 0 0 1 .232.056l3.686 1.894-.694-3.957a.564.564 0 0 1 .162-.505l2.907-2.77-4.052-.576a.525.525 0 0 1-.393-.288L8.001 2.223 8 2.226z';
const STAR_FULL =
  'M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z';

function StarGlyph({ d, size }: { d: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d={d} />
    </svg>
  );
}

export interface StarsProps {
  /** Rating value 0–5. Rendered to the nearest half-star (e.g. 3.5 = 3½). */
  value: number;
  /** Star size in px. Default 18. */
  size?: number;
  style?: CSSProperties;
}

export function Stars({ value, size = 18, style }: StarsProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 2,
        color: 'var(--color-stars, #EF8735)',
        ...style,
      }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        // How much of star `n` is filled (0–1), snapped to half-steps so a 3.5
        // shows three full stars + a half-filled fourth + an empty fifth.
        const frac = Math.max(0, Math.min(1, value - (n - 1)));
        const step = Math.round(frac * 2) / 2;
        const d = step === 1 ? STAR_FULL : step === 0.5 ? STAR_HALF : STAR_EMPTY;
        return <StarGlyph key={n} d={d} size={size} />;
      })}
    </span>
  );
}

export interface StarRatingProps {
  /** Current whole-star value 0–5. {@code 0} = all empty. */
  value: number;
  /** Called with the picked value. Clicking the active star again clears to 0. */
  onChange: (value: number) => void;
  /** Star size in px. Default 24. */
  size?: number;
  disabled?: boolean;
  /** Accessible label for the group. */
  'aria-label'?: string;
}

/**
 * Interactive 5-star picker — empty by default, hover previews the fill, click
 * sets the value (clicking the active star again clears it). Shares the exact
 * {@link Stars} glyph + `--color-stars` theme color so the input matches the
 * display.
 */
export function StarRating({
  value,
  onChange,
  size = 24,
  disabled = false,
  'aria-label': ariaLabel,
}: StarRatingProps) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  return (
    <span
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        gap: 4,
        color: 'var(--color-stars, #EF8735)',
      }}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n}`}
          disabled={disabled}
          onMouseEnter={() => !disabled && setHover(n)}
          onMouseLeave={() => setHover(0)}
          onFocus={() => !disabled && setHover(n)}
          onBlur={() => setHover(0)}
          onClick={() => !disabled && onChange(value === n ? 0 : n)}
          style={{
            display: 'inline-flex',
            padding: 0,
            border: 'none',
            background: 'none',
            color: 'inherit',
            cursor: disabled ? 'default' : 'pointer',
            lineHeight: 0,
          }}
        >
          <StarGlyph d={shown >= n ? STAR_FULL : STAR_EMPTY} size={size} />
        </button>
      ))}
    </span>
  );
}
